<template>
  <div>
    <v-alert type="warning"
             flat
             tile
             v-if="contact && contact.is_unsubscribed">
      <template v-if="team.resubscribe_contacts_enabled">
        You cannot email <strong>{{ contact.email }}</strong> because they are unsubscribed. You are allowed to <a href="#" class="white--text font-weight-bold" @click.prevent="resubscribeContact">resubscribe them</a>.
      </template>
      <template v-else>
        You cannot email <strong>{{ contact.email }}</strong> because they are unsubscribed.
      </template>
    </v-alert>
    <v-row>
      <v-col cols="12">
        <v-radio-group v-model="form.greeting_type"
                       label="Greeting Style"
                       dense
                       :disabled="form.busy"
                       class="mt-0">
          <v-radio value="Formal"
                   label="Professional">
          </v-radio>
          <v-radio value="Personal"
                   label="Personal">
          </v-radio>
        </v-radio-group>

        <div class="text-caption text--secondary">
          <div v-if="isGreetingFormal">
            Will use salutation + last name (e.g., "{{ greeting }}") or full name if no salutation is provided.
          </div>
          <div v-else>
            Will use first name only (e.g., "{{ greeting }}") for a more casual approach.
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12"
             sm="3">
        <v-select v-model="form.salutation"
                  label="Salutation"
                  outlined
                  dense
                  :placeholder="isGreetingFormal ? 'Required' : 'Optional'"
                  :clearable="isGreetingPersonal"
                  :hide-details="form.errors.has('salutation') === false"
                  :error-messages="form.errors.get('salutation')"
                  :rules="isGreetingFormal ? [rules.required] : []"
                  :items="salutations"
                  item-text="name"
                  item-value="id"
                  :disabled="form.busy">
        </v-select>
      </v-col>
      <v-col cols="12"
             sm="4">
        <v-text-field v-model="form.first_name"
                      autocomplete="fake-name"
                      label="First name"
                      placeholder="Required"
                      outlined
                      dense
                      :hide-details="form.errors.has('first_name') === false"
                      :error-messages="form.errors.get('first_name')"
                      :rules="[rules.required]"
                      required
                      :disabled="form.busy"
                      @change="changeSync">
        </v-text-field>
      </v-col>
      <v-col cols="12"
             sm="5">
        <v-text-field v-model="form.last_name"
                      autocomplete="fake-name"
                      label="Last name"
                      placeholder="Required"
                      outlined
                      dense
                      :hide-details="form.errors.has('last_name') === false"
                      :error-messages="form.errors.get('last_name')"
                      :rules="[rules.required]"
                      required
                      :disabled="form.busy"
                      @change="changeSync">
        </v-text-field>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-text-field v-model="form.email"
                      type="email"
                      autocomplete="fake-email"
                      label="Email"
                      placeholder="Required"
                      outlined
                      dense
                      :hide-details="form.errors.has('email') === false"
                      :error-messages="form.errors.get('email')"
                      :rules="[rules.required, rules.email]"
                      required
                      :disabled="form.busy">
        </v-text-field>
      </v-col>
    </v-row>

    <template v-if="(hasMedicalAttributes || isUserContactLevelEnabled) && contact && contact.crm_object && contact.crm_object_id">
      <v-row>
        <v-col cols="12">
          <v-text-field :value="contact.title"
                        label="Title (Read-only)"
                        outlined
                        dense
                        disabled
                        hide-details>
          </v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <template v-if="hasMedicalAttributes">

          <v-col cols="12"
                 sm="3">
            <v-text-field :value="contact.designation"
                          label="Designation (Read-only)"
                          outlined
                          dense
                          disabled
                          hide-details>
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="3">
            <v-text-field :value="contact.specialty"
                          label="Specialty (Read-only)"
                          outlined
                          dense
                          disabled
                          hide-details>
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="3">
            <v-text-field :value="contact.npi"
                          label="NPI (Read-only)"
                          outlined
                          dense
                          disabled
                          hide-details>
            </v-text-field>
          </v-col>
        </template>

        <v-col v-if="isUserContactLevelEnabled"
               cols="12"
               sm="3">
          <v-text-field :value="contact.level"
                        label="Level (Read-only)"
                        outlined
                        dense
                        disabled
                        hide-details>
          </v-text-field>
        </v-col>

        <template v-if="isUserContactTerritoryEnabled">
          <v-col cols="12"
                 sm="3">
            <v-text-field :value="contact.territory_id"
                          label="Territory ID (Read-only)"
                          outlined
                          dense
                          disabled
                          hide-details>
            </v-text-field>
          </v-col>

          <v-col cols="12"
                 sm="3">
            <v-text-field :value="contact.territory_name"
                          label="Territory Name (Read-only)"
                          outlined
                          dense
                          disabled
                          hide-details>
            </v-text-field>
          </v-col>
        </template>
      </v-row>

      <v-row>
        <v-col cols="12"
               sm="3">
          <v-text-field :value="contact.crm_object"
                        label="CRM Object (Read-only)"
                        outlined
                        dense
                        disabled
                        hide-details>
          </v-text-field>
        </v-col>

        <v-col cols="12"
               sm="3">
          <v-text-field :value="contact.crm_object_id"
                        label="CRM Object ID (Read-only)"
                        outlined
                        dense
                        disabled
                        hide-details>
          </v-text-field>
        </v-col>

        <v-col v-if="usesSalesforce"
               cols="12"
               sm="3">
          <v-text-field :value="contact.crm_record_type_name"
                        label="CRM Record Type (Read-only)"
                        outlined
                        dense
                        disabled
                        hide-details>
          </v-text-field>
        </v-col>

        <v-col v-if="team && team.user_contact_mdm_enabled"
               cols="12"
               sm="3">
          <v-text-field :value="contact.mdm_id"
                        label="MDM ID (Read-only)"
                        outlined
                        dense
                        disabled
                        hide-details>
          </v-text-field>
        </v-col>
      </v-row>
    </template>

    <template v-if="team.user_contact_company_name_enabled">
      <v-row>
        <v-col>
          <v-text-field v-model="form.company_name"
                        label="Company/Account (Optional)"
                        placeholder="Optional"
                        outlined
                        dense
                        hide-details
                        :error-messages="form.errors.get('company_name')"
                        :disabled="form.busy">
          </v-text-field>
        </v-col>
      </v-row>
    </template>

    <template v-if="team.user_contact_address_enabled">
      <v-row>
        <v-col>
          <template v-if="isAddressEditingDisabled">
            <v-text-field :value="form.country"
                          label="Country"
                          placeholder="Country"
                          outlined
                          dense
                          hide-details
                          disabled>
            </v-text-field>

          </template>
          <template v-else>
            <v-autocomplete v-model="form.country"
                            label="Country"
                            outlined
                            dense
                            hide-details
                            :items="countries"
                            item-value="alpha3"
                            item-text="name"
                            clearable>
            </v-autocomplete>
          </template>
        </v-col>
      </v-row>
      <template v-if="form.country">

        <v-row>
          <v-col>
            <v-text-field v-model="form.address1"
                          label="Street address line 1"
                          outlined
                          dense
                          hide-details
                          autocomplete="off"
                          :disabled="isAddressEditingDisabled">
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field v-model="form.address2"
                          label="Street address line 2"
                          outlined
                          dense
                          hide-details
                          autocomplete="off"
                          :disabled="isAddressEditingDisabled">
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="4">
            <v-text-field v-model="form.locality"
                          label="City"
                          outlined
                          dense
                          autocomplete="off"
                          :disabled="isAddressEditingDisabled">
            </v-text-field>
          </v-col>
          <v-col cols="4">
            <v-autocomplete v-if="isUSA"
                            v-model="form.administrative_area"
                            label="State"
                            outlined
                            dense
                            :items="states"
                            item-value="id"
                            item-text="name"
                            clearable
                            :disabled="isAddressEditingDisabled">
            </v-autocomplete>
            <v-text-field v-else
                          v-model="form.administrative_area"
                          label="Administrative area"
                          outlined
                          dense
                          autocomplete="off"
                          :disabled="isAddressEditingDisabled">
            </v-text-field>
          </v-col>
          <v-col cols="4">
            <v-text-field v-model="form.postal_code"
                          :label="isUSA ? 'ZIP Code' : 'Postal code'"
                          outlined
                          dense
                          autocomplete="off"
                          :disabled="isAddressEditingDisabled">
            </v-text-field>
          </v-col>
        </v-row>
      </template>
    </template>

    <v-switch v-if="canUseCRM"
              v-model="form.sync_from_crm"
              label="Synchronize salutation and name from CRM"
              hint="Turn this off if you want to keep the salutation and name you enter and ignore the values in CRM for this contact."
              persistent-hint
              :disabled="form.busy"
              dense
              class="mb-4">
    </v-switch>

    <v-switch v-model="form.is_active"
              :label="form.is_active ? 'This contact is active and can receive emails.' : 'This contact is inactive and cannot receive emails.'"
              hint="You can only send emails to active contacts. Turn this off if you do not want to send emails to this email address."
              persistent-hint
              :disabled="form.busy"
              dense>
    </v-switch>
  </div>
</template>
<script>
import {mapGetters} from 'vuex';
import {isInteger, isObject} from 'lodash';
import formValidation from '../../../plugins/formValidation';
import subscriberService from "../../../../../ml/services/subscriberService";
import DOMPurify from 'dompurify';

export default {
  mixins: [formValidation],
  props: {
    contact: {
      type: Object,
      required: false,
    },
    form: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      designations: [
        //{id: null, name: 'None'},
        {id: 'MD', name: 'MD'},
        {id: 'PhD', name: 'PhD'},
      ],
      lists: [],
      salutations: [
        //{id: null, name: 'None'},
        {id: 'Dr.', name: 'Dr.'},
        {id: 'Mr.', name: 'Mr.'},
        {id: 'Ms.', name: 'Ms.'},
        {id: 'Mrs.', name: 'Mrs.'},
        {id: 'Miss', name: 'Miss'},
        {id: 'Prof.', name: 'Prof.'},
      ],
      subscriber: null, // MLD-2851
    }
  },
  computed: {
    ...mapGetters([
      'canVerifyEmails', // MLD-6016
      'countries', // MLD-2872
      'states', // MLD-2872
      'hasMedicalAttributes', // MLD-3435
      'isUserContactLevelEnabled', // MLD-4405
      'isUserContactTerritoryEnabled', // MLD-4745
      'team',
      'user',
    ]),
    isGreetingFormal() { // MLD-5638
      return !this.isGreetingPersonal;
    },
    isGreetingPersonal() {
      return this.form.greeting_type === 'Personal';
    },
    greeting() {
      const parts = [];
      if (this.isGreetingFormal) {
        if (this.form.salutation && this.form.last_name) {
          parts.push(this.form.salutation);
        }
        if (this.form.first_name && !this.form.salutation) {
          parts.push(this.form.first_name);
        }
        if (this.form.last_name) {
          parts.push(this.form.last_name);
        }
        if (parts.length === 0) {
          parts.push('Dr. Smith');
        }
      } else if (this.form.first_name) {
        parts.push(this.form.first_name);
      } else {
        parts.push('John');
      }
      return parts.join(' ');
    },
    usesSalesforce() { // MLD-4827
      return this.team
        && this.team.uses_salesforce;
    },
    isAddressEditingDisabled() { // MLD-4827
      if (this.contact) {
        return this.contact.source !== 'ML';
      }
      return false;
    },
    canUseCRM() {
      return this.team.crm_enabled
        && this.team.has_crm;
    },
    isUSA() { // MLD-2872
      if (!this.form.country) { // MLD-4827
        return true;
      }
      return ['United States of America', 'USA', 'US'].includes(this.form.country);
    },
    contactHasID() { // MLD-5505
      return isObject(this.contact)
        && isInteger(this.contact.id);
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    fetchData() {
      this.getSubscriber(); // MLD-2851
      /*if (this.contact) {
        return mailcoachService.getLists({user_id: this.contact.user_id})
          .then(data => {
            this.lists = data;
          })
      }*/
    },
    changeSync() {
      if (this.contact) {
        this.form.sync_from_crm = false
      }
    },
    getSubscriber() { // MLD-2851
      if (!this.contact) {
        return;
      }
      return subscriberService.find(this.contact.email)
        .then(data => {
          this.subscriber = data;
        })
        .catch(err => {

        });
    },
    async resubscribeContact() { // MLD-2851
      if (!this.contact) {
        return;
      }
      if (!confirm(`Are you sure you want to resubscribe ${DOMPurify.sanitize(this.contact.email)}?`)) {
        return;
      }
      if (this.subscriber) {
        const form = {
          subscriber_id: [this.subscriber.id],
        }
        return subscriberService.resubscribe(form)
          .then(data => {
            this.$emit('updated');
          })
          .catch(() => {
          })
      }
    }
  }
}
</script>
